import React, { useRef, useState } from "react";
import { ELECTION_TYPES } from "../../lib/costEngine";
import { parseDocument } from "../../lib/api";
import { Card, CardContent } from "../ui/card";
import { UploadCloud, Sparkles, FileText } from "lucide-react";
import { toast } from "sonner";

// AI-parse dropzone: extracts event fields from an uploaded PDF/DOCX and
// reports them via onFields(payload). Used on the Event Details step.
export function EventUploadDropzone({ onFields }) {
  const [uploading, setUploading] = useState(false);
  const [fileName, setFileName] = useState("");
  const fileRef = useRef(null);

  const handleFile = async (file) => {
    if (!file) return;
    if (!/\.(pdf|docx)$/i.test(file.name)) {
      toast.error("Please upload a PDF or DOCX file.");
      return;
    }
    setFileName(file.name);
    setUploading(true);
    try {
      const res = await parseDocument(file);
      if (res.success) {
        const f = res.fields || {};
        const payload = {};
        if (f.eventTitle) payload.eventTitle = f.eventTitle;
        if (f.membershipSize) payload.membershipSize = String(f.membershipSize);
        if (f.electionType && ELECTION_TYPES[f.electionType]) payload.electionType = f.electionType;
        if (f.startDate) payload.startDate = f.startDate;
        if (f.endDate) payload.endDate = f.endDate;
        if (f.voterTurnout != null) payload.voterTurnout = String(f.voterTurnout);
        if (f.location) payload.location = f.location;
        onFields(payload);
        toast.success("Document parsed — fields auto-filled. Please review.");
      } else {
        toast.error(res.notes || "Could not extract data from this document.");
      }
    } catch (e) {
      toast.error("AI parsing failed. Enter details manually.");
    } finally {
      setUploading(false);
    }
  };

  return (
    <Card
      data-testid="upload-dropzone"
      onClick={() => !uploading && fileRef.current?.click()}
      onDragOver={(e) => e.preventDefault()}
      onDrop={(e) => {
        e.preventDefault();
        handleFile(e.dataTransfer.files?.[0]);
      }}
      className="border-2 border-dashed border-primary/30 bg-accent/40 cursor-pointer hover:border-primary/60 transition-colors"
    >
      <CardContent className="p-6">
        <input
          ref={fileRef}
          data-testid="file-input"
          type="file"
          accept=".pdf,.docx"
          className="hidden"
          onChange={(e) => handleFile(e.target.files?.[0])}
        />
        {uploading ? (
          <div className="space-y-3" data-testid="parsing-loader">
            <div className="flex items-center gap-2 text-primary font-medium">
              <Sparkles className="h-4 w-4 animate-pulse" />
              AI is parsing {fileName}…
            </div>
            <div className="h-3 rounded-md shimmer" />
            <div className="h-3 rounded-md shimmer w-2/3" />
          </div>
        ) : (
          <div className="flex items-center gap-4">
            <div className="h-12 w-12 rounded-xl bg-primary/10 flex items-center justify-center">
              {fileName ? <FileText className="h-6 w-6 text-primary" /> : <UploadCloud className="h-6 w-6 text-primary" />}
            </div>
            <div>
              <p className="font-600 text-foreground">
                {fileName ? fileName : "Upload a governing document or past proposal"}
              </p>
              <p className="text-sm text-muted-foreground">
                PDF or DOCX — AI extracts membership size, election type & more.
              </p>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
